import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useAuth } from "../contexts/AuthContext";
import { getMessageForFetchError } from "../shared/utils/apiError";
import {
  getRegistrationStatusKey,
  REGISTRATION_STATUS_CONFIG,
} from "../shared/utils/eventRegistrationMetrics";
import ErrorBanner from "../components/ErrorBanner";
import EventDashboardMetricsFilters from "../components/EventDashboardMetricsFilters";
import { buildEventsManagementPath, EVENT_MGMT_TAB_EVENTS } from "../shared/utils/eventManagementTabs";
import { filterEventsByManagementFilters } from "../shared/utils/eventManagementFilters";
import { resolveProgramChartColor } from "../shared/utils/programColor";
import { useEventProgramHostedByFilters } from "../shared/hooks/useEventProgramHostedByFilters";
import { useGlobalTimeNavigator } from "../shared/hooks/useGlobalTimeNavigator";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require("../uiConfig");

const TIME_PHASE_LABELS = {
  upcoming: "Current Month",
  remaining: "Beyond Current Month",
  closed: "Closed",
};

function getEventBucketByNavigatorMonth(ev, monthStart, monthEnd) {
  const status = String(ev?.status || "Open").trim().toLowerCase();
  if (status === "closed") return "closed";
  const start = ev?.start_datetime ? dayjs(ev.start_datetime) : null;
  const end = ev?.end_datetime ? dayjs(ev.end_datetime) : null;
  const a = start && start.isValid() ? start : (end && end.isValid() ? end : null);
  const b = end && end.isValid() ? end : (start && start.isValid() ? start : null);
  if (!a || !b) return null;
  // Current Month = any overlap with [monthStart, monthEnd]
  if (!(b.isBefore(monthStart) || a.isAfter(monthEnd))) return "upcoming";

  // Beyond Current Month = starts after this month ends (long-term horizon)
  // Note: We intentionally do NOT count past events here.
  if (start && start.isValid() && start.isAfter(monthEnd)) return "remaining";
  return null;
}

// Fill-rate segment order for "By fill rate" chart (Critical Low → Overbooked)
const FILL_RATE_ORDER = [
  "low_registration",
  "below_capacity",
  "almost_full",
  "full",
  "waitlist_active",
  "no_cap",
];


// Build monthly trend for closed events: attended %, no-show %, walk-ins %, waitlisted % and cancelled % vs registered count.
// IMPORTANT: Uses the same scope as drill-to Event Management participants tab from Closed Events Metrics:
// - Closed events only (status === "Closed")
// - End (or start) date within the last 12 months
// - Has attendance data (registered_count > 0 OR waitlisted_count > 0)
// "Registered" in the chart = total participants (participant_count) for those events.
function buildNoShowWalkInTrend(events, { last12MonthsOnly = true } = {}) {
  const rowsByMonth = {};

  const todayForPhase = dayjs().startOf("day");
  const twelveMonthsAgo = todayForPhase.subtract(12, "month");

  (events || []).forEach((ev) => {
    const status = String(ev?.status || "Open").trim().toLowerCase();
    if (status !== "closed") return;

    const dtRaw = ev.end_datetime || ev.start_datetime;
    const dt = dtRaw ? dayjs(dtRaw) : null;
    if (!dt || !dt.isValid()) return;
    if (last12MonthsOnly && dt.isBefore(twelveMonthsAgo)) return;

    const registered = ev.registered_count ?? 0;
    const waitlisted = ev.waitlisted_count ?? 0;
    // Require at least some attendance data, consistent with drill filter
    if (registered <= 0 && waitlisted <= 0) return;

    const totalParticipants = ev.participant_count != null
      ? ev.participant_count
      : (registered + waitlisted);

    if (totalParticipants <= 0) return;

    const noShow = ev.no_show_count ?? 0;
    const walkIn = ev.walk_in_count ?? 0;
    const cancelled = ev.cancelled_count ?? 0;
    const attended = ev.attended_count ?? 0;
    // Participants with status "Registered" (not yet Attended/No-Show/Walk-In/Waitlisted/Cancelled) so all segments sum to total
    const registeredStatus = Math.max(0, totalParticipants - (attended + noShow + walkIn + waitlisted + cancelled));

    const monthKey = dt.format("YYYY-MM");
    if (!rowsByMonth[monthKey]) {
      rowsByMonth[monthKey] = { registered: 0, noShow: 0, walkIn: 0, waitlisted: 0, cancelled: 0, attended: 0, registeredStatus: 0 };
    }

    // Registered bar = participant_count so dashboard matches drill (Participants) total
    rowsByMonth[monthKey].registered += totalParticipants;
    rowsByMonth[monthKey].noShow += noShow;
    rowsByMonth[monthKey].walkIn += walkIn;
    rowsByMonth[monthKey].waitlisted += waitlisted;
    rowsByMonth[monthKey].cancelled += cancelled;
    rowsByMonth[monthKey].attended += attended;
    rowsByMonth[monthKey].registeredStatus += registeredStatus;
  });

  const sortedMonths = Object.keys(rowsByMonth).sort();
  const lastMonths = sortedMonths.slice(-12); // last 12 months with data

  const trendRows = lastMonths.map((m) => {
    const { registered, noShow, walkIn, waitlisted, cancelled, attended, registeredStatus } = rowsByMonth[m];
    const base = registered > 0 ? registered : 1;
    // All percentages use total participants (base) so they sum to 100%
    const registeredPct = (registeredStatus / base) * 100;
    const noShowPct = (noShow / base) * 100;
    const walkInPct = (walkIn / base) * 100;
    const attendedPct = (attended / base) * 100;
    const waitlistedPct = (waitlisted / base) * 100;
    const cancelledPct = (cancelled / base) * 100;
    return {
      monthKey: m,
      label: dayjs(m + "-01").format("MMM YYYY"),
      registered,
      waitlisted,
      cancelled,
      attended,
      registeredStatus: registeredStatus ?? 0,
      noShowPct,
      walkInPct,
      attendedPct,
      waitlistedPct,
      cancelledPct,
      registeredPct,
    };
  });

  let overallRegistered = 0;
  let overallNoShow = 0;
  let overallWalkIn = 0;
  let overallWaitlisted = 0;
  let overallCancelled = 0;
  let overallAttended = 0;
  let overallRegisteredStatus = 0;

  trendRows.forEach((row) => {
    overallRegistered += row.registered;
    overallNoShow += row.noShowPct != null ? (row.noShowPct / 100) * row.registered : 0;
    overallWalkIn += row.walkInPct != null ? (row.walkInPct / 100) * row.registered : 0;
    overallWaitlisted += row.waitlisted;
    overallCancelled += row.cancelled ?? 0;
    overallAttended += row.attended ?? 0;
    overallRegisteredStatus += row.registeredStatus ?? 0;
  });

  const overallBase = overallRegistered > 0 ? overallRegistered : 1;
  const overallNoShowPct = (overallNoShow / overallBase) * 100;
  const overallWalkInPct = (overallWalkIn / overallBase) * 100;
  const overallWaitlistedPct = (overallWaitlisted / overallBase) * 100;
  const overallCancelledPct = (overallCancelled / overallBase) * 100;
  const overallAttendedPct = (overallAttended / overallBase) * 100;
  const overallRegisteredPct = (overallRegisteredStatus / overallBase) * 100;

  return {
    rows: trendRows,
    overall: {
      registered: overallRegistered,
      waitlisted: overallWaitlisted,
      cancelled: overallCancelled,
      attended: overallAttended,
      registeredStatus: overallRegisteredStatus,
      noShowPct: overallRegistered > 0 ? overallNoShowPct : null,
      walkInPct: overallRegistered > 0 ? overallWalkInPct : null,
      attendedPct: overallAttendedPct,
      waitlistedPct: overallWaitlistedPct,
      cancelledPct: overallCancelledPct,
      registeredPct: overallRegisteredPct,
    },
  };
}

// Primary brand indigo palette (replaces high-contrast red/blue)
const CLOSED_EVENTS_BAR_COLORS = {
  reg: "#312e81",   // indigo-900
  regPct: "#4338ca", // indigo-700 (Registered status / pending)
  att: "#4f46e5",   // indigo-600
  ns: "#6366f1",    // indigo-500
  wi: "#818cf8",    // indigo-400
  wl: "#a5b4fc",    // indigo-300
  cancelled: "#c7d2fe", // indigo-200
};

// Attendance type passed when drilling from a chart segment (null = no filter / Registered bar)
function NoShowWalkInTrendCard({ events, onDrillToEventParticipants, last12MonthsOnly = true }) {
  const [activeTab, setActiveTab] = React.useState("overall"); // "overall" | "last30"
  const { rows, overall } = React.useMemo(
    () => buildNoShowWalkInTrend(events || [], { last12MonthsOnly }),
    [events, last12MonthsOnly]
  );
  if (!rows.length) {
    return (
      <div
        style={{
          marginTop: "10px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          border: "1px solid #e5e7eb",
          padding: "16px 20px 20px",
        }}
      >
        <div
          style={{
            fontSize: "11px",
            fontWeight: 600,
            color: "#374151",
            marginBottom: "4px",
          }}
        >
          No-show, walk-in and cancelled trend
        </div>
        <div style={{ fontSize: "11px", color: "#6b7280" }}>
          No closed events with attendance data yet. Close events with
          attendance to see no-show, walk-in, waitlisted and cancelled trends.
        </div>
      </div>
    );
  }
  const formatPct = (v) =>
    v == null ? "—" : `${v.toFixed(1).replace(/\.0$/, "")}%`;

  const barHeightPx = 280;
  const columnWidthPx = 420;
  const barMeta = [
    { key: "reg", label: "Total", color: CLOSED_EVENTS_BAR_COLORS.reg, drillAttendance: null },
    { key: "regPct", label: "Registered %", color: CLOSED_EVENTS_BAR_COLORS.regPct, drillAttendance: "Registered" },
    { key: "att", label: "Attended %", color: CLOSED_EVENTS_BAR_COLORS.att, drillAttendance: "Attended" },
    { key: "ns", label: "No-show %", color: CLOSED_EVENTS_BAR_COLORS.ns, drillAttendance: "No-Show" },
    { key: "wi", label: "Walk-ins %", color: CLOSED_EVENTS_BAR_COLORS.wi, drillAttendance: "Walk-In" },
    { key: "wl", label: "Waitlisted %", color: CLOSED_EVENTS_BAR_COLORS.wl, drillAttendance: "Waitlisted" },
    { key: "cancelled", label: "Cancelled %", color: CLOSED_EVENTS_BAR_COLORS.cancelled, drillAttendance: "Cancelled" },
  ];

  const last30Row = rows.length ? rows[rows.length - 1] : null;
  const last30Group = last30Row
    ? {
        id: "last30",
        label: "Last 30 Days",
        values: [
          last30Row.registered,
          last30Row.registeredPct ?? 0,
          last30Row.attendedPct ?? 0,
          last30Row.noShowPct ?? 0,
          last30Row.walkInPct ?? 0,
          last30Row.waitlistedPct ?? 0,
          last30Row.cancelledPct ?? 0,
        ],
        waitlistedCount: last30Row.waitlisted ?? 0,
        cancelledCount: last30Row.cancelled ?? 0,
        attendedCount: last30Row.attended ?? 0,
        registeredStatusCount: last30Row.registeredStatus ?? 0,
      }
    : null;

  const chartGroups = [
    {
      id: "overall",
      label: "Overall",
      values: [
        overall.registered,
        overall.registeredPct ?? 0,
        overall.attendedPct ?? 0,
        overall.noShowPct ?? 0,
        overall.walkInPct ?? 0,
        overall.waitlistedPct ?? 0,
        overall.cancelledPct ?? 0,
      ],
      waitlistedCount: overall.waitlisted ?? 0,
      cancelledCount: overall.cancelled ?? 0,
      attendedCount: overall.attended ?? 0,
      registeredStatusCount: overall.registeredStatus ?? 0,
    },
    ...(last30Group ? [last30Group] : []),
  ];

  // Total = full height (100%); all % bars use same total so they sum to 100%
  const renderBarGroup = (group) => {
    const total = group.values[0];
    const registeredPct = group.values[1];
    const attendedPct = group.values[2];
    const noShowPct = group.values[3];
    const walkInPct = group.values[4];
    const waitlistedPct = group.values[5];
    const cancelledPct = group.values[6] ?? 0;
    const registeredStatusCount = group.registeredStatusCount != null ? group.registeredStatusCount : (total > 0 ? Math.round((registeredPct / 100) * total) : 0);
    const attendedCount = total > 0 ? Math.round((attendedPct / 100) * total) : 0;
    const noShowCount = total > 0 ? Math.round((noShowPct / 100) * total) : 0;
    const walkInCount = total > 0 ? Math.round((walkInPct / 100) * total) : 0;
    const waitlistedCount = group.waitlistedCount != null ? group.waitlistedCount : (total > 0 ? Math.round((waitlistedPct / 100) * total) : 0);
    const cancelledCount = group.cancelledCount != null ? group.cancelledCount : (total > 0 ? Math.round((cancelledPct / 100) * total) : 0);

    return (
    <div
      key={group.id}
      style={{
        flex: "0 0 auto",
        width: `${columnWidthPx}px`,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: "6px",
        padding: "4px",
        borderRadius: "8px",
      }}
    >
      {group.values.map((value, i) => {
        const meta = barMeta[i];
        const isTotal = meta.key === "reg";
        const height = isTotal
          ? (value > 0 ? barHeightPx : 0)
          : (Math.min(100, value) / 100) * barHeightPx;
        const countForBar = meta.key === "reg" ? total : meta.key === "regPct" ? registeredStatusCount : meta.key === "att" ? attendedCount : meta.key === "ns" ? noShowCount : meta.key === "wi" ? walkInCount : meta.key === "wl" ? waitlistedCount : meta.key === "cancelled" ? cancelledCount : 0;
        const displayVal =
          meta.key === "reg"
            ? (value > 0 ? `${value.toLocaleString()} (100%)` : "—")
            : value > 0 ? `${countForBar} (${formatPct(value)})` : "—";
        const barLabelText = meta.key === "reg" ? (value > 0 ? `${value.toLocaleString()} (100%)` : "—") : (value > 0 ? `${countForBar} (${formatPct(value)})` : "—");
        const showLabelOnBar = height >= 22;
        const showLabelAboveBar = value > 0 && !showLabelOnBar && !isTotal;
        const handleBarClick = onDrillToEventParticipants ? () => onDrillToEventParticipants(meta.drillAttendance) : undefined;
        const segmentDiv = (
          <div
            style={{
              height: `${Math.max(height, 2)}px`,
              backgroundColor: meta.color,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: height >= 20 ? "20px" : "2px",
              ...(handleBarClick && { cursor: "pointer" }),
            }}
            {...(handleBarClick && {
              role: "button",
              tabIndex: 0,
              onClick: handleBarClick,
              onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleBarClick(); } },
            })}
          >
            {showLabelOnBar && value > 0 && (
              <span style={{ fontSize: "9px", fontWeight: 700, color: "#fff", textShadow: "0 0 1px rgba(0,0,0,0.3)", whiteSpace: "nowrap" }}>
                {barLabelText}
              </span>
            )}
          </div>
        );
        return (
          <div
            key={meta.key}
            style={{
              flex: 1,
              minWidth: "52px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
            title={handleBarClick ? `${meta.label}: ${displayVal}. Click to view participants for closed events.` : `${meta.label}: ${displayVal}`}
          >
            <div
              style={{
                width: "100%",
                height: `${barHeightPx}px`,
                borderRadius: "4px 4px 0 0",
                backgroundColor: "#f9fafb",
                overflow: "visible",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                border: "1px solid #e5e7eb",
              }}
            >
              {showLabelAboveBar && (
                <div style={{ flexShrink: 0, textAlign: "center", marginBottom: "2px", minHeight: "14px" }}>
                  <span style={{ fontSize: "9px", fontWeight: 700, color: "#374151", whiteSpace: "nowrap" }}>{barLabelText}</span>
                </div>
              )}
              {segmentDiv}
            </div>
          </div>
        );
      })}
    </div>
  );
  };

  return (
    <div
      style={{
        marginTop: "0",
        backgroundColor: "#fff",
        borderRadius: "10px",
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        padding: "10px 14px 12px",
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", flexWrap: "wrap", width: "100%" }}>
        {/* Left: chart — grows to fill page width beside the guide column */}
        <div style={{ flex: "1 1 320px", minWidth: 0 }}>
          <div
            style={{
              display: "inline-flex",
              gap: "0",
              borderRadius: "9999px",
              backgroundColor: "#f3f4f6",
              padding: "2px",
              marginBottom: "8px",
            }}
          >
            <button
              type="button"
              onClick={() => setActiveTab("overall")}
              style={{
                padding: "8px 20px",
                borderRadius: "9999px",
                border: "none",
                fontSize: "13px",
                fontWeight: 600,
                color: activeTab === "overall" ? "#fff" : "#6b7280",
                backgroundColor: activeTab === "overall" ? "#4f46e5" : "transparent",
                cursor: "pointer",
                transition: "background-color 0.15s, color 0.15s",
              }}
            >
              Overall
            </button>
            {last30Group && (
              <button
                type="button"
                onClick={() => setActiveTab("last30")}
                style={{
                  padding: "8px 20px",
                  borderRadius: "9999px",
                  border: "none",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: activeTab === "last30" ? "#fff" : "#6b7280",
                  backgroundColor: activeTab === "last30" ? "#4f46e5" : "transparent",
                  cursor: "pointer",
                  transition: "background-color 0.15s, color 0.15s",
                }}
              >
                Last 30 Days
              </button>
            )}
          </div>

          <div
            style={{
              overflowX: "auto",
              padding: "8px 14px 6px 14px",
              minWidth: "min(100%, 1000px)",
            }}
          >
        <div style={{ display: "flex", alignItems: "stretch", gap: "0", minHeight: `${barHeightPx + 14}px` }}>
          <div style={{ flex: "1 0 auto", minWidth: "min-content" }}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                gap: "48px",
                minHeight: `${barHeightPx + 14}px`,
              }}
            >
              {activeTab === "last30" && last30Group
                ? renderBarGroup(last30Group)
                : renderBarGroup(chartGroups[0])}
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "16px",
            marginTop: "4px",
            paddingTop: "6px",
            borderTop: "1px solid #e5e7eb",
            fontSize: "11px",
            color: "#4b5563",
            flexWrap: "wrap",
          }}
        >
          {barMeta.map((meta) => (
            <span key={meta.key} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "3px",
                  backgroundColor: meta.color,
                  flexShrink: 0,
                }}
              />
              {meta.label}
            </span>
          ))}
        </div>
      </div>
        </div>

        {/* Right: time filter and metric definitions */}
        <div
          style={{
            flex: "0 0 260px",
            fontSize: "11px",
            color: "#4b5563",
            lineHeight: 1.4,
            padding: "8px 10px",
            backgroundColor: "#f8fafc",
            borderRadius: "6px",
            border: "1px solid #e2e8f0",
          }}
        >
          <div style={{ fontWeight: 600, color: "#374151", marginBottom: "4px", fontSize: "11px" }}>Time filter for each chart</div>
          <ul style={{ margin: 0, paddingLeft: "16px", marginBottom: "8px" }}>
            <li style={{ marginBottom: "2px" }}>
              <strong>Overall</strong> — Events that have already ended (closed), with end date in the last 12 months, and at least one participant (registered or waitlisted).
            </li>
            <li style={{ marginBottom: "2px" }}>
              <strong>Last 30 Days</strong> — Same definition as Overall but limited to events whose end date falls in the most recent calendar month with data.
            </li>
          </ul>
          <div style={{ fontWeight: 600, color: "#374151", marginBottom: "4px", fontSize: "11px" }}>What each metric means</div>
          <ul style={{ margin: 0, paddingLeft: "16px" }}>
            <li style={{ marginBottom: "2px" }}>
              <strong>Total</strong> — All participants in the selected time filter. All % bars use this as the denominator so they sum to 100%.
            </li>
            <li style={{ marginBottom: "2px" }}>
              <strong>Registered %</strong> — Share still marked Registered (not yet Attended/No-show/Walk-in/Waitlisted/Cancelled). (Registered ÷ Total) × 100.
            </li>
            <li style={{ marginBottom: "2px" }}>
              <strong>Attended %</strong> — Share who attended. (Attended ÷ Total) × 100.
            </li>
            <li style={{ marginBottom: "2px" }}>
              <strong>No-show %</strong> — Share who did not show up. (No-show ÷ Total) × 100.
            </li>
            <li style={{ marginBottom: "2px" }}>
              <strong>Walk-ins %</strong> — Share added at event. (Walk-ins ÷ Total) × 100.
            </li>
            <li style={{ marginBottom: "2px" }}>
              <strong>Waitlisted %</strong> — Share on waitlist. (Waitlisted ÷ Total) × 100.
            </li>
            <li style={{ marginBottom: "2px" }}>
              <strong>Cancelled %</strong> — Share who cancelled. (Cancelled ÷ Total) × 100.
            </li>
          </ul>
          <div style={{ marginTop: "4px", fontSize: "10px", color: "#64748b" }}>
            Only closed events (ended) with at least one participant are included in the chart and in drill-to Event Participants.
          </div>
        </div>
      </div>
    </div>
  );
}

function RegistrationMetrics() {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const account_id = userInfo?.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;
  const {
    programs,
    filterProgramId,
    setFilterProgramId,
    filterOrganizerId,
    setFilterOrganizerId,
    filterProgramMembers,
  } = useEventProgramHostedByFilters({ account_id });
  const timeNav = useGlobalTimeNavigator({ initialView: "all" });

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bannerError, setBannerError] = useState("");

  const loadEvents = useCallback(() => {
    if (!account_id) {
      setLoading(false);
      return;
    }
    setBannerError("");
    apiFetch(serverUrl + "/getEventsWithDetails", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setEvents(Array.isArray(data) ? data : []))
      .catch((err) => setBannerError(getMessageForFetchError(err)))
      .finally(() => setLoading(false));
  }, [account_id, serverUrl]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const filteredEvents = useMemo(
    () =>
      filterEventsByManagementFilters(events, {
        programId: filterProgramId,
        organizerId: filterOrganizerId,
        timeNav,
      }),
    [events, filterProgramId, filterOrganizerId, timeNav]
  );

  const focusDay = dayjs(timeNav.focusDateYmd || undefined).isValid()
    ? dayjs(timeNav.focusDateYmd).startOf("day")
    : dayjs().startOf("day");
  const monthStart = focusDay.startOf("month").startOf("day");
  const monthEnd = focusDay.endOf("month").endOf("day");

  const metricsFilterBar = (
    <EventDashboardMetricsFilters
      programs={programs}
      filterProgramId={filterProgramId}
      onProgramChange={setFilterProgramId}
      filterOrganizerId={filterOrganizerId}
      onOrganizerChange={setFilterOrganizerId}
      filterProgramMembers={filterProgramMembers}
      timeNav={timeNav}
    />
  );

  // Group events by program for stacked chart (like task dashboard by category)
  const groupedByProgram = React.useMemo(() => {
    const groups = {};
    filteredEvents.forEach((ev) => {
      const programId = (ev.program_id || "").toString().trim();
      const programName = (ev.program_name != null && ev.program_name !== "")
        ? ev.program_name
        : (programId ? `Program ${programId}` : "No program");
      const key = programId || "__none__";
      const displayName = programId ? programName : "No program";
      if (!groups[key]) {
        groups[key] = {
          groupKey: programId || "__none__",
          groupName: displayName,
          storedColor: ev.program_color,
          closed: 0,
          upcoming: 0,
          remaining: 0,
        };
      } else if (!groups[key].storedColor && ev.program_color) {
        groups[key].storedColor = ev.program_color;
      }
      const phase = getEventBucketByNavigatorMonth(ev, monthStart, monthEnd);
      if (!phase) return;
      groups[key][phase] += 1;
    });
    return Object.values(groups)
      .sort((a, b) => String(a.groupName).localeCompare(String(b.groupName)))
      .map((g, i) => ({
        ...g,
        color: resolveProgramChartColor({
          groupKey: g.groupKey,
          storedColor: g.storedColor,
          fallbackIndex: i,
        }),
      }));
  }, [filteredEvents, monthStart, monthEnd]);

  const stackedBars = React.useMemo(() => {
    if (groupedByProgram.length === 0) return null;
    return {
      upcomingSegments: groupedByProgram.map(({ groupName, groupKey, upcoming, color }) => ({
        groupName,
        groupKey,
        value: upcoming,
        color,
      })),
      remainingSegments: groupedByProgram.map(({ groupName, groupKey, remaining, color }) => ({
        groupName,
        groupKey,
        value: remaining,
        color,
      })),
      closedSegments: groupedByProgram.map(({ groupName, groupKey, closed, color }) => ({
        groupName,
        groupKey,
        value: closed,
        color,
      })),
    };
  }, [groupedByProgram]);

  // Group events by fill rate for "By fill rate" stacked chart (Critical Low / Below Target / Healthy / Full / Overbooked)
  const stackedBarsByFillRate = React.useMemo(() => {
    const byPhase = { upcoming: {}, remaining: {}, closed: {} };
    filteredEvents.forEach((ev) => {
      const phase = getEventBucketByNavigatorMonth(ev, monthStart, monthEnd);
      if (!phase) return;
      const key = getRegistrationStatusKey(ev) || "no_cap";
      if (!byPhase[phase][key]) byPhase[phase][key] = 0;
      byPhase[phase][key] += 1;
    });
    const buildSegments = (phaseKey) =>
      FILL_RATE_ORDER.filter((key) => (byPhase[phaseKey][key] || 0) > 0).map((key) => {
        const cfg = REGISTRATION_STATUS_CONFIG[key] || { label: "No cap", color: "#9ca3af" };
        return {
          groupKey: key,
          groupName: cfg.label,
          value: byPhase[phaseKey][key] || 0,
          color: cfg.color,
        };
      });
    return {
      upcomingSegments: buildSegments("upcoming"),
      remainingSegments: buildSegments("remaining"),
      closedSegments: buildSegments("closed"),
    };
  }, [filteredEvents, monthStart, monthEnd]);

  function handleDrilldown(timePhase, registrationStatus, programId) {
    const params = new URLSearchParams();
    const navDate = timeNav.focusDateYmd || dayjs().format("YYYY-MM-DD");
    if (timePhase === "remaining") {
      params.set("navView", "future");
      params.set("navDate", navDate);
      params.set("status", "open");
    } else if (timePhase === "closed") {
      params.set("navView", "all");
      params.set("navDate", navDate);
      params.set("status", "closed");
    } else if (timePhase === "upcoming") {
      params.set("navView", "month");
      params.set("navDate", navDate);
      params.set("status", "open");
    }
    if (registrationStatus) params.set("registrationStatus", registrationStatus);
    const drillProgram = programId != null && programId !== "" ? programId : (filterProgramId || "");
    if (drillProgram) params.set("program", drillProgram);
    if ((filterOrganizerId || "").trim()) params.set("organizer", filterOrganizerId.trim());
    navigate(buildEventsManagementPath({ tab: EVENT_MGMT_TAB_EVENTS, ...Object.fromEntries(params.entries()) }));
  }

  if (loading) {
    return (
      <div style={{ padding: "24px" }}>
        <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 600 }}>Registration Metrics</h2>
        {metricsFilterBar}
        <p style={{ marginTop: "12px", color: "#6c757d" }}>Loading…</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, minWidth: 0, overflowX: "hidden", overflowY: "auto" }}>
      <div style={{ flexShrink: 0, padding: "0 0 4px 0", borderBottom: "1px solid #dee2e6", marginBottom: "4px" }}>
        <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 600, color: "#212529" }}>Registration Metrics</h2>
        <p style={{ margin: "1px 0 0", fontSize: "11px", color: "#6c757d" }}>Event counts by time bucket and registration fill. Drill into Event Management with filters.</p>
      </div>
      {metricsFilterBar}
      <ErrorBanner message={bannerError} onDismiss={() => setBannerError("")} />

      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "12px 0 0" }}>
        <div style={{ width: "100%", display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(260px, 1fr)", gap: "12px", alignItems: "flex-start" }}>
          {/* Column 1: By program */}
          <div
            style={{
              minWidth: 0,
              backgroundColor: "#fff",
              borderRadius: "10px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              padding: "10px 14px 12px",
            }}
          >
            <div style={{ fontSize: "14px", fontWeight: 600, color: "#111827", marginBottom: "2px" }}>By program</div>
            <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "8px", lineHeight: 1.3 }}>Event counts by program and time bucket.</div>
            {stackedBars && groupedByProgram.length > 0 ? (
              <>
                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "8px", minHeight: "320px", padding: "4px 0 0", maxWidth: "100%" }}>
                  {[
                    { phaseKey: "upcoming", label: TIME_PHASE_LABELS.upcoming, segments: stackedBars.upcomingSegments },
                    { phaseKey: "remaining", label: TIME_PHASE_LABELS.remaining, segments: stackedBars.remainingSegments },
                    { phaseKey: "closed", label: TIME_PHASE_LABELS.closed, segments: stackedBars.closedSegments },
                  ].map(({ phaseKey, label, segments }) => {
                    const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
                    const barHeight = 260;
                    const minSegmentPx = 8;
                    return (
                      <div key={phaseKey} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", minWidth: 0 }}>
                        <div style={{ fontSize: "11px", fontWeight: 600, color: "#374151", marginBottom: "4px" }}>{segments.reduce((s, seg) => s + seg.value, 0)}</div>
                        <div
                          style={{
                            width: "52%",
                            minWidth: "40px",
                            height: `${barHeight}px`,
                            borderRadius: "4px 4px 0 0",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column-reverse",
                            border: "1px solid #e5e7eb",
                            backgroundColor: "#f9fafb",
                          }}
                        >
                          {segments.map((seg) => {
                            const rawHeight = total > 0 ? (seg.value / total) * barHeight : 0;
                            const segmentHeight = seg.value > 0 ? Math.max(rawHeight, minSegmentPx) : 0;
                            return (
                              <div
                                key={seg.groupName}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleDrilldown(phaseKey, null, seg.groupKey)}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleDrilldown(phaseKey, null, seg.groupKey); } }}
                                style={{
                                  height: `${segmentHeight}px`,
                                  minHeight: segmentHeight > 0 ? "6px" : 0,
                                  backgroundColor: seg.color,
                                  cursor: "pointer",
                                  flexShrink: 0,
                                  borderTop: "1px solid rgba(255,255,255,0.5)",
                                }}
                                title={`${seg.groupName}: ${seg.value} event(s)`}
                              />
                            );
                          })}
                        </div>
                        <div style={{ marginTop: "6px", fontSize: "10px", textAlign: "center", color: "#6b7280", maxWidth: "100%", lineHeight: 1.2 }}>{label}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", marginTop: "8px", fontSize: "11px", color: "#4b5563", borderTop: "1px solid #e5e7eb", paddingTop: "8px" }}>
                  {groupedByProgram.map(({ groupName, groupKey, color }) => (
                      <span key={groupKey || groupName} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ width: "12px", height: "12px", borderRadius: "3px", backgroundColor: color, flexShrink: 0 }} />
                        {groupName}
                      </span>
                  ))}
                </div>
              </>
            ) : (
              <p style={{ color: "#6c757d", fontSize: "13px", margin: 0 }}>No events. Add events in Event Management to see metrics by program.</p>
            )}
          </div>

          {/* Column 2: By fill rate */}
          <div
            style={{
              minWidth: 0,
              backgroundColor: "#fff",
              borderRadius: "10px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              padding: "10px 14px 12px",
            }}
          >
            <div style={{ fontSize: "14px", fontWeight: 600, color: "#111827", marginBottom: "2px" }}>By fill rate</div>
            <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "8px", lineHeight: 1.3 }}>Event counts by registration fill rate and time bucket.</div>
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "8px", minHeight: "320px", padding: "4px 0 0", maxWidth: "100%" }}>
              {[
                { phaseKey: "upcoming", label: TIME_PHASE_LABELS.upcoming, segments: stackedBarsByFillRate.upcomingSegments },
                { phaseKey: "remaining", label: TIME_PHASE_LABELS.remaining, segments: stackedBarsByFillRate.remainingSegments },
                { phaseKey: "closed", label: TIME_PHASE_LABELS.closed, segments: stackedBarsByFillRate.closedSegments },
              ].map(({ phaseKey, label, segments }) => {
                const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
                const barHeight = 260;
                const minSegmentPx = 8;
                return (
                  <div key={phaseKey} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", minWidth: 0 }}>
                    <div style={{ fontSize: "11px", fontWeight: 600, color: "#374151", marginBottom: "4px" }}>{segments.reduce((s, seg) => s + seg.value, 0)}</div>
                    <div
                      style={{
                        width: "52%",
                        minWidth: "40px",
                        height: `${barHeight}px`,
                        borderRadius: "4px 4px 0 0",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column-reverse",
                        border: "1px solid #e5e7eb",
                        backgroundColor: "#f9fafb",
                      }}
                    >
                      {segments.map((seg) => {
                        const rawHeight = total > 0 ? (seg.value / total) * barHeight : 0;
                        const segmentHeight = seg.value > 0 ? Math.max(rawHeight, minSegmentPx) : 0;
                        return (
                          <div
                            key={seg.groupKey}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleDrilldown(phaseKey, seg.groupKey)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleDrilldown(phaseKey, seg.groupKey); } }}
                            style={{
                              height: `${segmentHeight}px`,
                              minHeight: segmentHeight > 0 ? "6px" : 0,
                              backgroundColor: seg.color,
                              cursor: "pointer",
                              flexShrink: 0,
                              borderTop: "1px solid rgba(255,255,255,0.5)",
                            }}
                            title={`${seg.groupName}: ${seg.value} event(s)`}
                          />
                        );
                      })}
                    </div>
                    <div style={{ marginTop: "6px", fontSize: "10px", textAlign: "center", color: "#6b7280", maxWidth: "100%", lineHeight: 1.2 }}>{label}</div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", marginTop: "8px", fontSize: "11px", color: "#4b5563", borderTop: "1px solid #e5e7eb", paddingTop: "8px" }}>
              {FILL_RATE_ORDER.map((key) => {
                const cfg = REGISTRATION_STATUS_CONFIG[key] || { label: "No cap", color: "#9ca3af" };
                return (
                  <span key={key} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ width: "12px", height: "12px", borderRadius: "3px", backgroundColor: cfg.color, flexShrink: 0 }} />
                    {cfg.label}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Event Capacity Status Guide (3rd column) */}
          <div style={{ minWidth: 0, backgroundColor: "#fff", borderRadius: "10px", border: "1px solid #e5e7eb", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", overflow: "hidden" }}>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ color: "#009E73", display: "inline-flex", alignItems: "center" }} aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ display: "block" }}>
                  <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                </svg>
              </span>
              <strong style={{ fontSize: "12px", color: "#212529" }}>Event Capacity Status Guide</strong>
            </div>
            <div style={{ padding: "8px 12px 12px", display: "flex", flexDirection: "column", gap: 0 }}>
              {[
                { key: "low_registration", range: "<40%", description: "Registration is critically low.", action: "Promote the event, send reminders, or consider rescheduling if needed." },
                { key: "below_capacity", range: "40-79%", description: "Spots are still available.", action: "Share the event again and encourage referrals to increase sign-ups." },
                { key: "almost_full", range: "80-99%", description: "Event is nearing capacity.", action: "Prepare logistics and notify participants that spots are limited." },
                { key: "full", range: "100%", description: "Registration limit reached.", action: "Enable waitlist or consider opening another session." },
                { key: "waitlist_active", range: ">100%", description: "Event is over capacity.", action: "Add additional staff, split into groups, or schedule a new session." },
              ].map(({ key, range, description, action }, idx) => {
                const cfg = REGISTRATION_STATUS_CONFIG[key] || { label: key, color: "#9ca3af" };
                const label = `${cfg.label} (${range})`;
                return (
                  <div key={key} style={{ borderTop: idx === 0 ? "none" : "1px solid #e9ecef", paddingTop: idx === 0 ? 0 : "10px", marginTop: idx === 0 ? 0 : "10px" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                      <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: cfg.color, flexShrink: 0, marginTop: "4px" }} aria-hidden="true" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: "11px", color: "#212529", marginBottom: "2px" }}>{label}</div>
                        <div style={{ fontSize: "11px", color: "#495057", marginBottom: "4px" }}>{description}</div>
                        <div style={{ fontSize: "10px", color: "#6c757d" }}>{action}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegistrationMetrics;
export { NoShowWalkInTrendCard };
